import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class AudioService {
  private unlocked = false;
  private audioMap = new Map<string, HTMLAudioElement>();
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      this.unlockAudio();
    }
  }

  /** 🔓 Unlock browser audio policy */
  private unlockAudio(): void {
    const unlock = () => {
      if (this.unlocked || this.audioMap.size === 0) return;

      // 👉 Use FIRST registered audio to unlock
      const audio = Array.from(this.audioMap.values())[0];

      audio.volume = 0; // silent unlock
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.volume = 1;

          this.unlocked = true;
        })
        .catch(() => {});

      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
      document.removeEventListener('touchstart', unlock);
    };

    document.addEventListener('click', unlock);
    document.addEventListener('keydown', unlock);
    document.addEventListener('touchstart', unlock);
  }

  /** 🎵 Register audio once */
  register(key: string, src: string): void {
    if (!this.isBrowser || this.audioMap.has(key)) return;

    const audio = new Audio(src);
    audio.preload = 'auto';
    this.audioMap.set(key, audio);
  }

  /** ▶️ Play safely */
  play(key: string): void {
    if (!this.isBrowser) return;

    const audio = this.audioMap.get(key);
    if (!audio) return;

    // 🔥 auto-unlock fallback
    if (!this.unlocked) {
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          this.unlocked = true;
          audio.play();
        })
        .catch(() => {});
      return;
    }

    audio.currentTime = 0;
    audio.play().catch(() => {});
  }

  /** 🔇 Stop */
  stop(key: string): void {
    if (!this.isBrowser) return;

    const audio = this.audioMap.get(key);
    if (!audio) return;

    audio.pause();
    audio.currentTime = 0;
  }
}
